import { useDispatch, useSelector } from 'react-redux'

import { EuiSpacer, EuiTabbedContent } from '@elastic/eui'
import { EuiTabbedContentTab } from '@elastic/eui/src/components/tabs/tabbed_content/tabbed_content'

import { resetSearchImage } from 'components/chooseImage/searchImageSlice'
import { useCampaignBuilderActions } from 'features/builders/createCampaign/useCampaignBuilderActions'
import AgencyV2BillboardAdDrawer from 'features/campaigns/campaign/AgencyV2EditCampaignPage/AgencyV2BillboardAdDrawer'
import { CreateAdStep, CreateAdStyle, setAd, setCreateAdStep, setCreateAdStyle } from 'features/campaigns/createAd/internet/retail/createAdInternetRetailSlice'
import history from 'services/HistoryService'

import { CampaignSubType, CampaignType } from '../../../../api/interfaces'
import { RootState } from '../../../../app/rootReducer'
import { RetargetingDrawer } from '../RetargetingDrawer'
import AudioAdsDrawer from '../audio/AdsDrawer'
import InternetAdsDrawer from '../internet/AdsDrawer'
import TvAdsDrawer from '../tv/AdsDrawer'
import { EditCampaignDetails } from './EditCampaignDetails'
import { EditAudienceTargeting } from './forms/EditAudienceTargeting'
import { EditAudioLocationTargeting } from './forms/EditAudioLocationTargeting'
import { EditBillboardLocationTargeting } from './forms/EditBillboardLocationTargeting'
import { EditInternetLocationTargeting } from './forms/EditInternetLocationTargeting'
import { EditTvLocationTargeting } from './forms/EditTvLocationTargeting'
import { EditUploadedAudience } from './forms/EditUploadedAudience'

export const CampaignTabs = () => {
  const { builderTvUploadSelected, builderBillboardUploadSelected, builderAudioUploadSelected } = useCampaignBuilderActions()

  const { currentAccount } = useSelector((state: RootState) => state.app)

  const { campaign } = useSelector((state: RootState) => state.campaign)

  const dispatch = useDispatch()

  const getAdsTabName = () => {
    switch (campaign?.type) {
      case CampaignType.Billboard:
        return 'Digital Billboard Ads'
      case CampaignType.Internet:
        if (campaign?.campaignSubType === CampaignSubType.Display) {
          return 'Internet Display Ads'
        } else if (campaign?.campaignSubType === CampaignSubType.Native) {
          return 'Internet Native Ads'
        } else {
          return 'Internet Ads'
        }
      case CampaignType.TV:
        return 'TV Commercials'
      case CampaignType.Audio:
        return 'Streaming Audio Ads'
      default:
        return 'Ads'
    }
  }

  if (!campaign) {
    return null
  }

  const isRetargeting =
    campaign.advancedTargeting?.advancedTargetingType === 'Retarget' ||
    campaign.tvTargeting?.tvTargetingType === 'Retarget' ||
    campaign.audioTargeting?.audioTargetingType === 'Retarget'

  const isAudienceUploadCampaign =
    campaign.advancedTargeting?.advancedTargetingType === 'AudienceUpload' ||
    campaign.tvTargeting?.tvTargetingType === 'AudienceUpload' ||
    campaign.audioTargeting?.audioTargetingType === 'AudienceUpload'

  const supportsTargetingTabs = campaign.type === CampaignType.Internet || campaign.type === CampaignType.TV || campaign.type === CampaignType.Audio

  const tabs: Array<EuiTabbedContentTab> = [
    {
      id: 'details',
      name: 'Campaign Details',
      content: <EditCampaignDetails />
    },
    {
      id: 'ads',
      name: getAdsTabName(),
      content: (
        <>
          {campaign.type === CampaignType.Billboard && (
            <AgencyV2BillboardAdDrawer
              CreateAdButtonText='Upload Billboard'
              OnCreateAdClick={() => {
                builderBillboardUploadSelected()
                history.push('/campaigns/billboards/public/upload')
              }}
            />
          )}
          {campaign.type === CampaignType.Internet && (
            <InternetAdsDrawer
              CreateAdButtonText={campaign.campaignSubType === CampaignSubType.Display ? 'Upload Display Ad' : 'Create Native Ad'}
              OnCreateAdClick={() => {
                if (campaign.campaignSubType === CampaignSubType.Display) {
                  dispatch(setCreateAdStyle(CreateAdStyle.Upload))
                  history.push('/campaigns/internet/public/upload')
                } else {
                  dispatch(setCreateAdStep(CreateAdStep.Start))
                  dispatch(resetSearchImage())
                  dispatch(
                    setAd({
                      id: '',
                      headline: '',
                      body: '',
                      companyName: currentAccount?.name ?? '',
                      callToAction: 'Learn More',
                      destinationUrl: currentAccount?.website ?? ''
                    })
                  )
                  history.push('/campaigns/internet/public/start')
                }
              }}
            />
          )}
          {campaign.type === CampaignType.TV && (
            <TvAdsDrawer
              CreateAdButtonText='Upload TV Commercial'
              OnCreateAdClick={() => {
                builderTvUploadSelected()
                history.push('/campaigns/tv/public/upload')
              }}
            />
          )}
          {campaign.type === CampaignType.Audio && (
            <AudioAdsDrawer
              CreateAdButtonText='Upload Streaming Audio Ad'
              OnCreateAdClick={() => {
                builderAudioUploadSelected()
                history.push('/campaigns/audio/public/upload')
              }}
            />
          )}
        </>
      )
    },
    ...(!isRetargeting && !isAudienceUploadCampaign
      ? [
          {
            id: 'geoTargeting',
            name: 'Geo-Targeting',
            content: (
              <>
                <EuiSpacer />
                {campaign.type === CampaignType.TV && <EditTvLocationTargeting />}
                {campaign.type === CampaignType.Internet && <EditInternetLocationTargeting />}
                {campaign.type === CampaignType.Billboard && <EditBillboardLocationTargeting />}
                {campaign.type === CampaignType.Audio && <EditAudioLocationTargeting />}
              </>
            )
          }
        ]
      : [])
  ]

  if (!isRetargeting && !isAudienceUploadCampaign && supportsTargetingTabs) {
    tabs.push({
      id: 'audienceTargeting',
      name: 'Audience Targeting',
      content: (
        <>
          <EuiSpacer />
          <EditAudienceTargeting />
        </>
      )
    })
  }

  if (isRetargeting && supportsTargetingTabs) {
    tabs.push({
      id: 'retargeting',
      name: 'Retargeting',
      content: <RetargetingDrawer />
    })
  }

  if (isAudienceUploadCampaign && supportsTargetingTabs) {
    tabs.push({
      id: 'uploadedAudience',
      name: 'Uploaded Audience',
      content: (
        <>
          <EuiSpacer />
          <EditUploadedAudience />
        </>
      )
    })
  }

  return <EuiTabbedContent tabs={tabs} />
}
